import { useMsal } from "@azure/msal-react";
import { 
    DirectionalHint, 
    IColumn,
    Stack,
    IStackProps,
    useTheme,
    Text
} from "@fluentui/react";
import { RootState } from "app/store";
import { IGridItem } from "components/controls/CustomDetailList/IGridItem";
import LookupControlBase from "components/controls/LookupControlBase";
import LookupControlDialog, { TSearchPredicateAsync } from "components/controls/LookupControlDialog";
import { appStyles, createNonStandardTemplateColumns, emitCustomEvent, EViewType, fadeIn, friendlyValue} from "global/Application";
import { useApplication } from "global/Application/Context";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import PageLoading from "global/PageLoading";
import { addUpdateNonStandards, INonStandardPayload } from "global/Reducer/dataSlice";
import { ArrayToLookupByPredicate, ArrayToLookupByPredicateWithMap, formatDate, parseDate } from "global/Utility/TSFunctions";
import {
    bcbs_groupconfigurationsnapshot as IXrmTypes_bcbs_groupconfigurationsnapshot
} from "model/CRM";
import {
    IAccountsReceivable as FAM_IAccountsReceivable,
    IBenefit as FAM_IBenefit,
    IFeesAndPremiums as FAM_IFeesAndPremiums,
    IGroupConfigurationData as FAM_IGroupConfigurationData,
    INonStandard as FAM_INonStandard,
    INonStandardTemplate as FAM_INonStandardTemplate,
    IObjectWithNameId as FAM_IObjectWithNameId,
    ITemplateRelationship as FAM_ITemplateRelationship,
    TGuid as FAM_TGuid
} from "model/FlexibleAttributeModel";
import { EntityType, findEntityByCategoryId, getEntityDisplayName, findNonStandardInEntity, createEntityUpdatePayload, IEntityLookupResult, getNonStandardsFromEntity } from 'global/Utility/EntityHelpers';
import { useEffect, useMemo, useState } from "react";
import { connect, ConnectedProps, useDispatch } from "react-redux";
import { AppDataSource } from "../../global/DataSources/AppDataSource";
import InformationCallout from "components/controls/InformationCallout";
import NonStandardEditor, { EVariant } from "./NonStandardEditor";
import { ConfirmationDialog } from "../../components/controls/ConfirmationDialog";
import { EControlType } from "../../components/ControlWrapper";
import { setProcessing } from "../../global/Reducer/statusSlice";
import saveAs from "file-saver";
import NonStandardNonGridView from "../Comparison/NonStandardNonGridView";
import { ESaveType, SaveAsync, WithRecycle } from "../../global/Application/dataActions";
import { useHistory } from "react-router";
import { $att_$non_standard_$bcbs_benefitdefinition_id, $att_$non_standard_$bcbs_doesthisbenefitpayaccoridngtotherelatedbe_id, $att_$non_standard_$bcbs_effectivedate_id, $att_$non_standard_template_$bcbs_nst_benefitdefinition_id, $att_$non_standard_template_$bcbs_nst_doesthisbenefitpayaccoridngtotherelatedbe_id } from "model/ConfigAttributeConstants";

interface IProps extends TComponentConnectedProps {
    //TODO: unmount component with delay for animation
    isOpen: boolean;

    //nonStandardTemplate: FAM.INonStandardTemplate;

    benefit?: FAM_IBenefit;
    groupConfiguration?: FAM_IGroupConfigurationData;
    accountsReceivable?: FAM_IAccountsReceivable;
    feesAndPremium?: FAM_IFeesAndPremiums;

    nonStandardTemplate: FAM_INonStandardTemplate | null;


    onDismiss: () => void
}

enum ENonstandardScope {
    GroupConfiguration,
    Benefit,
    AccountsReceivable,
    FeesAndPremiums
}

enum ENonstandardTemplateScope {
    GroupConfiguration,
}


interface IScopeSearchResult extends FAM_IObjectWithNameId {
    entityType: EntityType;
    groupConfiguration: FAM_IGroupConfigurationData | null;
    benefit: FAM_IBenefit | null;
    accountsReceivable?: FAM_IAccountsReceivable;
    feesAndPremium?: FAM_IFeesAndPremiums;
    templateRelationShip: FAM_INonStandardTemplate | null;
}

interface INonstandardTemplateRelationshipScope {
    relationship: FAM_ITemplateRelationship;
    parent: FAM_IGroupConfigurationData;
    type: ENonstandardTemplateScope;
}

interface INonstandardSearchResult extends FAM_IObjectWithNameId {
    item: FAM_INonStandard;
    entityType?: EntityType;
    benefit?: FAM_IBenefit;
    accountsReceivable?: FAM_IAccountsReceivable;
    feesAndPremium?: FAM_IFeesAndPremiums;
}

interface INonstandardScope {
    nonStandard: FAM_INonStandard;
    parent: FAM_IBenefit | FAM_IGroupConfigurationData | FAM_IAccountsReceivable | FAM_IFeesAndPremiums;
    type: ENonstandardScope;
}

interface IImportNonstandardContext {
    snapshot?: IXrmTypes_bcbs_groupconfigurationsnapshot | null;
    json?: FAM_IGroupConfigurationData | null;
    nonStandardSearchResult?: INonstandardScope[] | null;
    nonStandardTemplateRelationshipSearchResult?: INonstandardTemplateRelationshipScope[] | null;
    scopeSearchResult?: IScopeSearchResult[] | null;
    searching?: boolean;
}

const NonStandardTemplateView = (props: IProps) => {
    // current view state/stages
    const [alertIsMounted, setAlertIsMounted] = useState(false);
    const [nonStandardTemplate, setNonStandardTemplate] = useState<FAM_INonStandardTemplate | null>(null);

    const [alertIsOpen, _setAlertIsOpen] = useState(false);
    const setAlertIsOpen: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
        if (!alertIsMounted)
            setAlertIsMounted(true);
        _setAlertIsOpen(value);
    }

    const [sourceContext, setSourceContext] = useState<IImportNonstandardContext | null>({
        nonStandardSearchResult: null,
        scopeSearchResult: null,
        snapshot: props.state.data.snapshot,
        json: props.state.data.json,
    });

    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);
    const state = props.state;
    const categoryNonStandardTemplate = props.state.data.config.cache.map_bcbs_config_category_named['Non-Standard Template'];

    const dispatch = useDispatch();
    const application = useApplication();
    const history = useHistory();

    const canChangeNS = props.state.status.viewType === EViewType.Modify &&
    (( application.ChangeTypePermissions.benefit.add_nonstandard) ||
    ( application.ChangeTypePermissions.canAddGroupConfigurationNonstandard));

    useEffect(() => {
        if (props.isOpen) {
            setSourceContext({
                snapshot: props.state.data.snapshot,
                json: props.state.data.json,
                scopeSearchResult: null,
            });
        }
        // if(props.nonStandardTemplate)
        // {
        //     setNonStandardTemplate(props.nonStandardTemplate)
        // }
    }, [props.isOpen, props.nonStandardTemplate]);

    /**
   * Search for all available non-standards inside a snapshot and output list of scopes
   * Group Configuration, Benefit 1, Benefit 2, ...
   * @param value 
   * @returns 
   */
    const onSearchScopes: TSearchPredicateAsync<INonstandardSearchResult> = async (value) => {
        const searchValue = value.toLowerCase() ?? '';

        if (!sourceContext?.snapshot || !sourceContext.json)
            throw new Error('expecting snapshot to search');

        //setNonStandardTemplate(props.nonStandardTemplate)
        return new Promise((resolve, reject) => {

            let json = sourceContext.json;

            let items: IGridItem<INonstandardSearchResult>[] = [];

            if (!json)
                reject(new Error('unexpected'));
            let key = 0;
            props.nonStandardTemplate?.relationships?.forEach((relationship, ix) => {
                let add = false;
                const scope = `Benefits`;// (${benefit.bcbs_name})/${benefit.name}`;
                const search = scope.toLowerCase();
                
                // Use helper to find entity by category ID
                const entityLookup = findEntityByCategoryId(props.state.data.json, relationship.categoryID);
                const categoryName = entityLookup ? getEntityDisplayName(entityLookup) : "";
                const entityType: EntityType = entityLookup?.entityType ?? 'benefit';

                 //if we are not searching for anything, return as/is
                if (!searchValue)
                    add = true;

                 //lets look into search
                if (scope && search.indexOf(searchValue) > -1)
                    add = true;

                if (add && entityLookup) {
                    var nonStandard: FAM_INonStandard | null = null;
                    
                    relationship.sectionRefs?.forEach(section => {
                        debugger;
                        let sectionId = section.sectionId;
                        section.nonStandards?.forEach(nonStandardRef => {
                            key += 1 
                            console.log(key)
                            
                            // Use helper to find non-standard in entity
                            const foundNonStandard = findNonStandardInEntity(entityLookup, nonStandardRef.id);
                            if (foundNonStandard) {
                                nonStandard = foundNonStandard;
                            }

                            if (nonStandard){ //&& //!nonStandard.deactivated) {
                                var sectionName = props.state.data.config.cache.map_bcbs_config_section[nonStandard.sectionRef!.id];
                                var tabName = props.state.data.config.cache.map_bcbs_config_tab[sectionName._bcbs_config_tabid_value!];
                                var configSection = props.state.data.config.cache.map_bcbs_config_section[sectionId];

                                const nsFields = ArrayToLookupByPredicateWithMap(nonStandard!.fields, t => t.configRef.id, t => {
                                    const attribute = props.state.data.config.cache.map_bcbs_config_attribute[t.configRef.id];
                                    const masterAttribute = props.state.data.config.cache.map_bcbs_config_masterattribute[attribute._bcbs_config_masterattributeid_value!];
                                    const controlType = masterAttribute.bcbs_attributetypecode as EControlType;
                                    return {
                                        field: t,
                                        attribute,
                                        masterAttribute,
                                        controlType,
                                    }
                                });
                                const effectiveDate = nsFields[$att_$non_standard_$bcbs_effectivedate_id];
                                let effectiveDateValue = "";

                                if (effectiveDate.field.values[0].value !== null) {
                                    effectiveDateValue = formatDate(parseDate(friendlyValue(effectiveDate.controlType, effectiveDate.field.values[0].value), 'local:yyyy-MM-dd'), 'en-US-ET');
                                }
                                
                                // Use helper to get display name
                                const displayCategoryName = getEntityDisplayName(entityLookup);

                                const item: IGridItem<INonstandardSearchResult> = {
                                    key: key,
                                    _index: ix,
                                    name: nonStandardRef.name,
                                    scope: configSection.bcbs_name,
                                    tab: tabName.bcbs_name,
                                    effectiveDate: effectiveDateValue,
                                    deactivated: nonStandard.deactivated  === true ? "Yes" : "No",
                                    category: displayCategoryName,
                                    data: {
                                        name: nonStandardRef.name,
                                        id: nonStandardRef.id,
                                        item: nonStandard,
                                        entityType: entityType,
                                        benefit: entityLookup.benefit,
                                        accountsReceivable: entityLookup.accountsReceivable,
                                        feesAndPremium: entityLookup.feesAndPremium,
                                    }
                                };

                                items.push(item);
                            }

                        })
                    })
                }

            });

            const quickViewColumn: IColumn = {
                key: 'view',
                minWidth: 50,
                name: 'View',
                fieldName: 'view',
                isResizable: true,
                onRender(item: IGridItem<INonstandardSearchResult>, index, column) {
                    return onRenderInformationCallout(item)
                },
            };

            // generating a temporary non-standard to match the schema
            const columns = createNonStandardTemplateColumns(
                props.state.data.config,
                {
                    category: categoryNonStandardTemplate,
                    linkedCategory: null,
                    config: props.state.data.config,
                },
                undefined,
                undefined,
                (field, configAttribute, configMasterAttribute) => {

                    // https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=ff72f3f2-d38d-ec11-b400-0022480a9a56
                    if (configAttribute?.bcbs_config_attributeid === $att_$non_standard_$bcbs_benefitdefinition_id)
                        return true;

                    return false;
                }
            );

            resolve({
                columns: [
                    {
                        key: 'category',
                        minWidth: 200,
                        name: 'Benefit',
                        fieldName: 'category',
                        isResizable: true,
                    },
                    {
                        key: 'scope',
                        minWidth: 200,
                        name: 'Section',
                        fieldName: 'scope',
                        isResizable: true,
                    },
                    {
                        key: 'deactivated',
                        minWidth: 200,
                        name: 'Deactivated',
                        fieldName: 'deactivated',
                        isResizable: true,
                    },
                    {
                        key: 'name',
                        minWidth: 350,
                        name: 'Name',
                        fieldName: 'name',
                        isResizable: true,
                    },
                    {
                        key: 'tab',
                        minWidth: 100,
                        name: 'Tab',
                        fieldName: 'tab',
                        isResizable: true,
                    },
                    ...columns,
                    quickViewColumn,
                ],
                items: items,
                showNoResultPrompt: items.length === 0,
                showLimitedResultPrompt: false,

            })
        });
    }


    const onRenderInformationCallout = (auditData?: IGridItem<INonstandardSearchResult>) => {

        debugger;
        if (!auditData?.data?.item)
            return <></>;

        const nonStandard = auditData?.data?.item;
        const benefit = auditData?.data?.benefit
            ? auditData?.data?.benefit
            : undefined;

        return <InformationCallout
            hover
            postText={auditData.compareKeys}
            directionalHint={DirectionalHint.rightCenter}
            calloutClassName={appStyles.calloutView}
        >
            <div style={{ margin: '10px' }}>
                <NonStandardEditor
                    variant={EVariant.viewer}
                    isOpen={true}
                    defaultValue={nonStandard}
                    benefit={benefit}
                    disabled={true}
                />

            </div>
        </InformationCallout>;
    }

    function onRenderBody(): JSX.Element {
        return <div className={fadeIn}>
            {onRenderValidationDialog()}
            <Stack tokens={{ childrenGap: 10 }}>

                {sourceContext?.searching &&
                    <PageLoading />
                }

                <LookupControlBase
                    label='Entity'
                    value={sourceContext?.scopeSearchResult?.[0]?.name}
                    disabled
                    onLookup={() => {
                        setSourceContext(q => {
                            return {
                                json: null,
                                nonStandardSearchResult: null,
                                scopeSearchResult: null,
                                snapshot: null
                            }
                        });
                    }}
                />

                <LookupControlDialog
                    isOpen={sourceContext?.snapshot !== null &&
                        sourceContext?.json !== null &&
                        sourceContext?.scopeSearchResult === null
                    }
                    label={'Non-Standard'}
                    onSearch={onSearchScopes}
                    variant="multiple"
                    splitButton={canChangeNS}
                    onSecondaryActionPick={(picks) => {
                        //deactivate selected NS
                        let nonstandardPayloads: INonStandardPayload[] = []
                        let deactivateNS: boolean = true
                        picks?.forEach(pick => {
                            if(pick.item.deactivated) 
                            {
                                //reactivate NS if it is deactivated
                                deactivateNS = false;
                            }
                            if (props.nonStandardTemplate?.relationships && props.nonStandardTemplate.relationships.length > 0) {
                                props.nonStandardTemplate.relationships.forEach(category => {
                                    let categoryID = category.categoryID;
                                    category.sectionRefs?.forEach(section => {
                                        section.nonStandards?.forEach(templateNS => {
                                            if (templateNS.id === pick.id) {
                                                const templateNSId = templateNS.id;
                                                //check group configuration 
                                                if (props.state.data.json.id === categoryID) {
                                                    //deactivate GC nonstandards
                                                    const foundNonStandard = props.state.data.json.nonStandards.find(ns => ns.id === templateNSId);
                                                    if (foundNonStandard) {
                                                        const updatedNonStandard: FAM_INonStandard = structuredClone(foundNonStandard);
                                                        updatedNonStandard.deactivated = deactivateNS;

                                                        const nonStandardPayload: INonStandardPayload = {
                                                            config: props.state.data.config,
                                                            nonStandards: [updatedNonStandard],
                                                            //benefit: props.benefit,
                                                            groupConfiguration: props.state.data.json,
                                                            changeTracker: {}
                                                        }
                                                        nonstandardPayloads.push(nonStandardPayload)
                                                    }
                                                }
                                                //check benefits
                                                const benefit = props.state.data.json.benefits.find(ben => ben.id === categoryID);
                                                if (benefit) {
                                                    //deactivate nonstandards
                                                    const foundNonStandard = benefit.nonStandards.find(ns => ns.id === templateNSId);
                                                    if (foundNonStandard) {
                                                        const updatedNonStandard: FAM_INonStandard = structuredClone(foundNonStandard);
                                                        updatedNonStandard.deactivated = deactivateNS;
                                                        const nonStandardPayload: INonStandardPayload = {
                                                            config: props.state.data.config,
                                                            nonStandards: [updatedNonStandard],
                                                            benefit: benefit,
                                                            //groupConfiguration: props.groupConfiguration,
                                                            changeTracker: {}
                                                        }
                                                        nonstandardPayloads.push(nonStandardPayload)
                                                    }
                                                }
                                                //check accounts receivable
                                                const ar = props.state.data.json.claimsARs?.find(a => a.id === categoryID);
                                                if (ar) {
                                                    const foundNonStandard = ar.nonStandards.find(ns => ns.id === templateNSId);
                                                    if (foundNonStandard) {
                                                        const updatedNonStandard: FAM_INonStandard = structuredClone(foundNonStandard);
                                                        updatedNonStandard.deactivated = deactivateNS;
                                                        const nonStandardPayload: INonStandardPayload = {
                                                            config: props.state.data.config,
                                                            nonStandards: [updatedNonStandard],
                                                            accountsReceivable: ar,
                                                            changeTracker: {}
                                                        }
                                                        nonstandardPayloads.push(nonStandardPayload)
                                                    }
                                                }
                                                //check fees and premiums
                                                const fp = props.state.data.json.feesAndPremiums?.find(f => f.id === categoryID);
                                                if (fp) {
                                                    const foundNonStandard = fp.nonStandards.find(ns => ns.id === templateNSId);
                                                    if (foundNonStandard) {
                                                        const updatedNonStandard: FAM_INonStandard = structuredClone(foundNonStandard);
                                                        updatedNonStandard.deactivated = deactivateNS;
                                                        const nonStandardPayload: INonStandardPayload = {
                                                            config: props.state.data.config,
                                                            nonStandards: [updatedNonStandard],
                                                            feesAndPremium: fp,
                                                            changeTracker: {}
                                                        }
                                                        nonstandardPayloads.push(nonStandardPayload)
                                                    }
                                                }
                                            }

                                        })
                                    })
                                });
                            }
                        })
                        if (nonstandardPayloads.length > 0)
                        {
                            dispatch(addUpdateNonStandards(nonstandardPayloads))
                        }

                        setSourceContext(q => {
                            return {
                                ...q,
                                snapshot: null,
                                nonStandardTemplateRelationshipSearchResult: null,
                                scopeSearchResult: null,
                                
                            }
                        });
                        
                        props.onDismiss();
                    }}
                    onPick={(picks) => {

                        // nothing to do
                        if (!picks || picks.length < 1) {
                            setSourceContext(q => {
                                return {
                                    json: null,
                                    nonStandardTemplateSearchResult: null,
                                    scopeSearchResult: null,
                                    snapshot: null
                                }
                            });
                            return;
                        }
                        if(picks.length > 1) return;
                        picks?.forEach(pick => {
                            //navigate to non Standard

                            let path = `/records/blueRelationship/${props.state.data.snapshot._bcbs_groupid_value}`;

                            path += `/groupConfiguration/${props.state.data.snapshot.bcbs_groupconfigurationsnapshotid}`;

                            if (pick.benefit)
                                path += `/benefit/${pick.benefit.id}`;
                            else if (pick.accountsReceivable)
                                path += `/accountsReceivable/${pick.accountsReceivable.id}`;
                            else if (pick.feesAndPremium)
                                path += `/feesAndPremium/${pick.feesAndPremium.id}`;

                            var section = props.state.data.config.cache.map_bcbs_config_section[pick!.item!.sectionRef!.id];
                            var tab = props.state.data.config.cache.map_bcbs_config_tab[section._bcbs_config_tabid_value!];
                            if (tab)
                                path += `/tab/${tab.bcbs_config_tabid}`;

                            if (section)
                                path += `/section/${section.bcbs_config_sectionid}`;

                            path += `/nonStandard/${pick.item.id}`;


                            dispatch(
                                WithRecycle(() => {
                                    history?.push({ pathname: path, search: '?navigation=pending' });
                                }, 100)
                            );

                        });
                    }}
                    onCancel={() => {

                        setSourceContext(q => {
                            return {
                                ...q,
                                snapshot: null,
                                nonStandardTemplateRelationshipSearchResult: null,
                                scopeSearchResult: null,
                                
                            }
                        });
                        props.onDismiss();
                    }}
                    onDismiss={() => {
                        setSourceContext(q => {
                            return {
                                ...q,
                                snapshot: null,
                                nonStandardTemplateRelationshipSearchResult: null,
                                scopeSearchResult: null,
                            }
                        });

                        props.onDismiss();
                    }}

                />
            </Stack>
        </div>
    }

    const onRenderValidationDialog = () => {
        if (!alertIsMounted)
            return <></>;

        return <ConfirmationDialog
            onContinue={() => {
                // (async () => {
                //     try {
                //         dispatch(setProcessing(true));
                //         const excelExtractImport = await import("global/ExcelExtract")
                //         const { NonstandardExcelGeneration } = excelExtractImport;
                //         const result = await NonstandardExcelGeneration(props.state, dataSource)
                //         const fileName = `NSE-${props.state.data.snapshot?.bcbs_name}(${props.state.data.snapshot?.bcbs_groupconfigurationsnapshotid})-${new Date().getTime()}.xlsx`;
                //         saveAs(result.file, fileName);
                //     } catch (error) {
                //         emitCustomEvent({
                //             eventType: 'globalException',
                //             details: error
                //         });
                //     } finally {
                //         dispatch(setProcessing(false));
                //     }
                // })();
                setAlertIsOpen(false); setAlertIsMounted(false); props.onDismiss?.();
                setTimeout(()=>{
                    SaveAsync(
                        dispatch,
                        dataSource,
                        props.state,
                        {saveType: ESaveType.EAuto});
                },100)
            }}
            onClose={() => { setAlertIsOpen(false); setAlertIsMounted(false); props.onDismiss?.(); }}
            continueText="Close"
            //cancelText="Close"
        >
            <Text>
            <p>Non-Standards have been updated or added as a result of this template action. </p>
            <p>You can open then NSE extract to view all Non-Standards and their changes.</p>
            </Text>
        </ConfirmationDialog>;
    }

    function matchingNonStandard(incomingNonStandard: FAM_INonStandard, incomingTemplate: FAM_INonStandardTemplate, categoryToCheck: FAM_ITemplateRelationship) {
        let returnIds: FAM_TGuid[] = [];
        const templatedNonStandard = structuredClone(incomingNonStandard);
        const template = structuredClone(incomingTemplate);
        const templateFieldMap = ArrayToLookupByPredicate(template.fields, q => q.configRef.id);
        let incomingNSCategoryId = categoryToCheck.categoryID;
        const nonStandardFields = ArrayToLookupByPredicateWithMap(templatedNonStandard!.fields, t => t.configRef.id, t => {
            const attribute = props.state.data.config.cache.map_bcbs_config_attribute[t.configRef.id];
            const masterAttribute = props.state.data.config.cache.map_bcbs_config_masterattribute[attribute._bcbs_config_masterattributeid_value!];
            const controlType = masterAttribute.bcbs_attributetypecode as EControlType;
            return {
                field: t,
                attribute,

                masterAttribute,
                controlType,
            }
        });
        const nonStandardDefinition = nonStandardFields[$att_$non_standard_$bcbs_benefitdefinition_id];
        let nonStandardDefinitionValue = "";

        if (nonStandardDefinition.field.values[0].value !== null) {
            nonStandardDefinitionValue = friendlyValue(nonStandardDefinition.controlType, nonStandardDefinition.field.values[0].value);
        }

        let formattedNonStandardDefinition = nonStandardDefinitionValue.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
        //take incoming category id

        // Use helper to find entity by category ID
        const entityLookup = findEntityByCategoryId(props.state.data.json, incomingNSCategoryId);
        if (!entityLookup) return returnIds;

        //only find section refs of section ref on ns
        categoryToCheck.sectionRefs?.forEach(section => {
            if (section.sectionId === templatedNonStandard.sectionRef?.id) {

                // --- ID-based match (primary) ---
                // If the template relationship already tracks NS ids for this section, check whether
                // any of those ids still exist on the entity. When found, return them directly
                // without falling through to the text scan. This prevents a re-apply after a verbiage
                // update from creating a duplicate.
                if (section.nonStandards && section.nonStandards.length > 0) {
                    const trackedIds = new Set(section.nonStandards.map(ns => ns.id));
                    const allNonStandards = getNonStandardsFromEntity(entityLookup);

                    allNonStandards.forEach(nonStandard => {
                        if (
                            trackedIds.has(nonStandard.id) &&
                            nonStandard.sectionRef?.id === section.sectionId &&
                            nonStandard.deactivated !== true
                        ) {
                            returnIds.push(nonStandard.id);
                        }
                    });

                    // If we found any id-based match, skip the text scan for this section
                    if (returnIds.length > 0) return;
                }

                // --- Text-based match (fallback for first-time apply) ---
                // Get all non-standards from the entity using helper
                const nonStandards = getNonStandardsFromEntity(entityLookup);

                //go through each category non standard, make sure section ref is equal to sectionID, then compare
                nonStandards.forEach(nonStandard => {
                    if (nonStandard.sectionRef?.id === section.sectionId && nonStandard.deactivated !== true) {
                        //we are in the section
                        //get non standard definition
                        const currentNonStandardFieldMap = ArrayToLookupByPredicate(nonStandard.fields, q => q.configRef.id);
                        const currentNonStandardDefinition = currentNonStandardFieldMap[$att_$non_standard_$bcbs_benefitdefinition_id];
                        let currentNonStandardDefinitionValue = friendlyValue(nonStandardDefinition.controlType, currentNonStandardDefinition.values[0].value);
                        let formattedCurrentNonStandardDefinitionValue = currentNonStandardDefinitionValue.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

                        //compare they are the same
                        if (formattedNonStandardDefinition === formattedCurrentNonStandardDefinitionValue) {
                            //add id to list if they are the same
                            returnIds.push(nonStandard.id);
                        }
                    }
                })
            }
        })

        return returnIds;
    }

    function updateAllTemplatedNonStandardsFields(incomingTemplate: FAM_INonStandardTemplate) {
        //read template fields
        //clone the template
        const template = structuredClone(incomingTemplate);
        const templateFields = ArrayToLookupByPredicateWithMap(template!.fields, t => t.configRef.id, t => {
            const attribute = props.state.data.config.cache.map_bcbs_config_attribute[t.configRef.id];
            const masterAttribute = props.state.data.config.cache.map_bcbs_config_masterattribute[attribute._bcbs_config_masterattributeid_value!];
            const controlType = masterAttribute.bcbs_attributetypecode as EControlType;
            return {
                field: t,
                attribute,

                masterAttribute,
                controlType,
            }
        });
        const templateDefinition = templateFields[$att_$non_standard_template_$bcbs_nst_benefitdefinition_id];
        const templatePayAccordingToBenefit = templateFields[$att_$non_standard_template_$bcbs_nst_doesthisbenefitpayaccoridngtotherelatedbe_id];
        let templateDefinitionValue = "";
        let templatePayAccordingToBenefitValue = "";


        if (templateDefinition.field.values[0].value !== null) {
            templateDefinitionValue = friendlyValue(templateDefinition.controlType, templateDefinition.field.values[0].value);
        }
        if (templatePayAccordingToBenefit.field.values[0].value !== null) {
            templatePayAccordingToBenefitValue = friendlyValue(templatePayAccordingToBenefit.controlType, templatePayAccordingToBenefit.field.values[0].value);
        }

        //go through each connected NS and update the fields on that.
        template?.relationships?.forEach(rel => {
            let categoryId = rel.categoryID;
            
            // Use helper to find entity by category ID
            const entityLookup = findEntityByCategoryId(props.state.data.json, categoryId);
            if (!entityLookup) return;

            rel.sectionRefs?.forEach(sectionRef => {
                sectionRef.nonStandards?.forEach(nonStandardRef => {
                    // Use helper to find non-standard in entity
                    const foundNonStandard = findNonStandardInEntity(entityLookup, nonStandardRef.id);
                    
                    if (foundNonStandard) {
                        //update fields
                        const updatedNonStandard: FAM_INonStandard = structuredClone(foundNonStandard);
                        updatedNonStandard.partOfTemplate = true;
                        updatedNonStandard.name = template.name;

                        let nonStandardBenefitDefintion = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_benefitdefinition_id);
                        let nonStandardPayAccordingToBenefit = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_doesthisbenefitpayaccoridngtotherelatedbe_id);

                        const benefitDefinitionValue = templateDefinition.field.values[0].value; //template value
                        nonStandardBenefitDefintion!.values[0].value = structuredClone(benefitDefinitionValue); //ns value

                        if (nonStandardPayAccordingToBenefit) {
                            const payAccordingToBenefitValue = templatePayAccordingToBenefit.field.values[0].value; //template value
                            nonStandardPayAccordingToBenefit!.values[0].value = structuredClone(payAccordingToBenefitValue); //ns value
                        }

                        // Use helper to create the correct entity payload
                        const entityPayload = createEntityUpdatePayload(entityLookup.entityType, entityLookup.entity);
                        
                        dispatch(addUpdateNonStandards([{
                            config: props.state.data.config,
                            nonStandards: [updatedNonStandard],
                            ...entityPayload,
                            changeTracker: {}
                        }]));
                    }
                })
            })
        })
    }

    return onRenderBody()

}




function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(NonStandardTemplateView);



